import { useState } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { User, Building2, Globe, Bell, Shield, LogOut } from "lucide-react";
import { toast } from "sonner";

export const SettingsPage = () => {
  const { user, token, logout } = useAuth();
  const { language, setLanguage } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState("");

  const createOrganization = async () => {
    if (!orgName) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/organizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ name: orgName })
      });

      if (res.ok) {
        toast.success(language === "sv" ? "Organisation skapad!" : "Organization created!");
        setOrgName("");
        window.location.reload();
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6 max-w-3xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            {language === "sv" ? "Inställningar" : "Settings"}
          </h1>
          <p className="text-slate-600 mt-1">
            {language === "sv" 
              ? "Hantera ditt konto och preferenser" 
              : "Manage your account and preferences"}
          </p>
        </div>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <User className="w-5 h-5" />
              {language === "sv" ? "Profil" : "Profile"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-white text-xl">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-slate-900">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <p className="text-xs text-slate-400 capitalize mt-1">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Building2 className="w-5 h-5" />
              {language === "sv" ? "Organisation" : "Organization"}
            </CardTitle>
            <CardDescription>
              {language === "sv" 
                ? "Hantera din organisations inställningar" 
                : "Manage your organization settings"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {user?.organization_id ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">
                  {language === "sv" ? "Ansluten till organisation" : "Connected to organization"}
                </p>
                <p className="text-sm text-green-600 mt-1">{user.organization_id}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-slate-600">
                  {language === "sv" 
                    ? "Du är inte ansluten till någon organisation. Skapa en ny eller be om inbjudan." 
                    : "You are not connected to any organization. Create a new one or request an invitation."}
                </p>
                <div className="flex gap-3">
                  <Input
                    placeholder={language === "sv" ? "Organisationsnamn" : "Organization name"}
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    data-testid="org-name-input"
                  />
                  <Button onClick={createOrganization} disabled={!orgName || loading} data-testid="create-org-btn">
                    {language === "sv" ? "Skapa" : "Create"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Language Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Globe className="w-5 h-5" />
              {language === "sv" ? "Språk" : "Language"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sv">Svenska</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Notifications Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Bell className="w-5 h-5" />
              {language === "sv" ? "Notifikationer" : "Notifications"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {language === "sv" ? "E-postnotifikationer" : "Email notifications"}
                </p>
                <p className="text-sm text-slate-500">
                  {language === "sv" ? "Få uppdateringar via e-post" : "Receive updates via email"}
                </p>
              </div>
              <Switch defaultChecked data-testid="email-notifications-switch" />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {language === "sv" ? "Push-notifikationer" : "Push notifications"}
                </p>
                <p className="text-sm text-slate-500">
                  {language === "sv" ? "Mobilnotifikationer" : "Mobile notifications"}
                </p>
              </div>
              <Switch data-testid="push-notifications-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Shield className="w-5 h-5" />
              {language === "sv" ? "Säkerhet" : "Security"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full sm:w-auto">
              {language === "sv" ? "Ändra lösenord" : "Change password"}
            </Button>
            <Separator />
            <Button 
              variant="destructive" 
              className="w-full sm:w-auto"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {language === "sv" ? "Logga ut" : "Log out"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};
